import type { ColorPalette, ThemeColorGroupName } from '@/types/theme';

export const COLOR_GROUP: ThemeColorGroupName[] = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error'
];

export const colorPalette: ColorPalette = {
  light: {
    primary: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#E9ECFE',
        '75': '#DBDFFF',
        '100': '#BFC7FF',
        '200': '#8B99F4',
        '300': '#5A6DE9',
        '400': '#3B51E2',
        '500': '#1A31D5',
        '600': '#1226B6',
        '700': '#1024A3',
        '800': '#1A2991',
        '900': '#0F2080'
      }
    },
    secondary: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#F1F3F9',
        '75': '#E6EAF4',
        '100': '#DDE2F0',
        '200': '#BFC7D9',
        '300': '#9FA9C2',
        '400': '#7F8BAA',
        '500': '#606E92',
        '600': '#4A567A',
        '700': '#364263',
        '800': '#232E4C',
        '900': '#111A35'
      }
    },
    info: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#EAFFFF',
        '75': '#DFFBFB',
        '100': '#D4F6F7',
        '200': '#BFF2F4',
        '300': '#94E6E8',
        '400': '#58D0D2',
        '500': '#16B3B8',
        '600': '#13A4A8',
        '700': '#159499',
        '800': '#14888C',
        '900': '#0C797D'
      }
    },
    success: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#EDF9F1',
        '75': '#DEF5E4',
        '100': '#C2EBD4',
        '200': '#8FD8AF',
        '300': '#59C68D',
        '400': '#35B270',
        '500': '#149457',
        '600': '#117D49',
        '700': '#0F693D',
        '800': '#0C502E',
        '900': '#083820'
      }
    },
    warning: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#FFF2E0',
        '75': '#FFEACF',
        '100': '#FFDEB4',
        '200': '#FFD39D',
        '300': '#FFBC75',
        '400': '#FC973F',
        '500': '#F97316',
        '600': '#EA5B0C',
        '700': '#CC4A05',
        '800': '#B84307',
        '900': '#AD3C05'
      }
    },
    error: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#FDF0F1',
        '75': '#FCE8EA',
        '100': '#FDD9DD',
        '200': '#FFC7CB',
        '300': '#FF98A0',
        '400': '#F2717E',
        '500': '#E53945',
        '600': '#CC2E3A',
        '700': '#B02531',
        '800': '#921D27',
        '900': '#8C1B25'
      }
    },
    gray: {
      shades: {
        main: '500'
      },
      variants: {
        '0': '#FFFFFF',
        '10': '#FBFBFD',
        '25': '#F6F6F8',
        '50': '#F3F3F5',
        '75': '#EDEDF0',
        '100': '#E3E3E5',
        '200': '#D3D4D7',
        '300': '#C4C4C8',
        '400': '#B4B5BA',
        '500': '#96979E',
        '600': '#777881',
        '700': '#62636A',
        '800': '#585A65',
        '900': '#464851',
        '1000': '#22303E',
        '1200': '#161B21'
      }
    }
  },
  dark: {
    primary: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#E6EDFF',
        '75': '#D5DBFF',
        '100': '#C7CFFF',
        '200': '#B3BFFF',
        '300': '#93A4FF',
        '400': '#8094FF',
        '500': '#7086FF',
        '600': '#5970FF',
        '700': '#435CFF',
        '800': '#374EF5',
        '900': '#3046E5'
      }
    },
    secondary: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#F0F3F9',
        '75': '#E1E6F0',
        '100': '#D2D9E8',
        '200': '#BCC7DB',
        '300': '#A1AFCC',
        '400': '#8EA1C2',
        '500': '#7D8FB3',
        '600': '#697BA1',
        '700': '#56678C',
        '800': '#435378',
        '900': '#314063'
      }
    },
    info: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#E6FCF9',
        '75': '#C9F7F0',
        '100': '#A8F0E6',
        '200': '#82E0D9',
        '300': '#56D1C6',
        '400': '#37C2B6',
        '500': '#2DA99B',
        '600': '#268F83',
        '700': '#21756B',
        '800': '#20665F',
        '900': '#1C5C54'
      }
    },
    success: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#E8FAF0',
        '75': '#D1F5E2',
        '100': '#B1EDCE',
        '200': '#82E0B1',
        '300': '#56D994',
        '400': '#39C47E',
        '500': '#2DA96A',
        '600': '#268F5A',
        '700': '#217549',
        '800': '#1C5C3A',
        '900': '#1A422D'
      }
    },
    warning: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#FFF8EB',
        '75': '#FEF0D6',
        '100': '#FDE5B8',
        '200': '#FDCB82',
        '300': '#FCB34F',
        '400': '#F9921D',
        '500': '#E87900',
        '600': '#D66B16',
        '700': '#B85610',
        '800': '#943A00',
        '900': '#752E00'
      }
    },
    error: {
      shades: {
        darker: '700',
        dark: '600',
        main: '500',
        soft: '400',
        softer: '300'
      },
      variants: {
        '50': '#FFF5F5',
        '75': '#FEEBEB',
        '100': '#FCD9D9',
        '200': '#FBA3A9',
        '300': '#F9858D',
        '400': '#F76A73',
        '500': '#E73F4A',
        '600': '#CC2E3A',
        '700': '#B02531',
        '800': '#9C1F2A',
        '900': '#8F1C25'
      }
    },
    gray: {
      shades: {
        main: '500'
      },
      variants: {
        '0': '#000000',
        '10': '#232333',
        '25': '#2B2C40',
        '50': '#313246',
        '75': '#373B50',
        '100': '#41455F',
        '200': '#545876',
        '300': '#6D718A',
        '400': '#898DA6',
        '500': '#A4A8BD',
        '600': '#B8BDD0',
        '700': '#CDD0E1',
        '800': '#E6E6F1',
        '900': '#F3F4FA',
        '1000': '#FAFAFD',
        '1200': '#FFFFFF'
      }
    }
  }
};
